/**
 * Store Pinia des lots (composition API)
 */
import { defineStore } from "pinia";
import { ref, computed } from "vue";

import { ILot } from "../models/lot";
import { ILotApiResponse } from "../models/api/lotApiResponse";
import { IProductionApiResponse } from "../models/api/productionApiResponse";
import { ILotFilter } from "../models/filters/lotFilter";
import { ILotForm } from "../models/forms/lotForm";
import LotService from "../services/LotService";
import ProductionService from "../services/ProductionService";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { IParameter } from "../models/parameter";
import { IProduction } from "../models/production";
import { ICenter } from "../models/center";
import { IDispatchManufactoringForm } from "../models/forms/dispatchManufactoringForm";
import { IProductionAnalyse } from "../models/productionAnalyse";
import { IProductionAnalyseForm } from "../models/forms/productionAnalyseForm";
import { getDateNumericFormat } from "../helper/dateFormatter";
import { useProductionAnalyseStore } from "./productionAnalyse.store";
import { useDispatchManufactoringStore } from "./dispatchManufactoring.store";
import { useManufactoringProductionStore } from "./manufactoringProduction.store";
import { useCenterStore } from "./center.store";
import { useParameterStore } from "./parameter.store";
import { useProductionStore } from "./production.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import type { IEjaculateCompositeResponse, IEjaculateDispatchResponse } from "../models/api/ejaculateCompositeApiResponse";
import type { IDispatchManufactoring } from "../models/dispatchManufactoring";
import type { IManufactoringProduction } from "../models/manufactoringProduction";
import type { IProductionStatic } from "../models/productionStatic";
import type { IProductionSample } from "../models/productionSample";
import type { IBoar } from "../models/boar";
import type { IProductionDilutorBath } from "../models/productionDilutorBath";

/**
 * Store Pinia des lots utilisant la Composition API
 */
export const useLotStore = defineStore("lots", () => {
    // State
    const datas = ref<ILot[]>([]);
    const current = ref<ILot>({} as ILot);
    const itemForm = ref<ILotForm>({} as ILotForm);
    const isLoading = ref(false);
    const loadedCenterId = ref<number | undefined>(undefined);
    const loadedDateFrom = ref<Date | undefined>(undefined);
    // Le centre d'origine des lots externes n'est résolu par le serveur que sur demande
    // (ILotFilter.includeProducerCenter, opt-in). Un cache rempli SANS lui ne porte pas
    // producerCenterCode : le Suivi de qualité, seul écran qui affiche cette colonne, doit
    // pouvoir le savoir, sinon il réutilise ce cache et laisse la colonne « Centre » vide.
    const loadedWithProducerCenter = ref(false);
    // Lots demandés par id que l'API ne rend pas : sans cette mémoire, hydrateDispatchManufactorings
    // les recalcule « manquants » et les redemande à chaque hydratation — or il en fait une par
    // changement d'onglet, par enregistrement et par message SignalR. Purgée à chaque load().
    const unresolvableLotIds = new Set<number>();
    const volumeUsedFabricationMap = ref<Map<number, number>>(new Map());
    const volumeRemainingMap = ref<Map<number, number | undefined>>(new Map());

    // Compteur incrémenté après chaque événement SignalR MajLot — force la réévaluation
    // des computed externes (ex: computedRowData dans EjaculateListView)
    const signalRVersion = ref(0);

    // Getters

    /**
     * Retourne le volume utilisé par les dispatches pour un lot donné (pré-calculé côté serveur)
     */
    const getVolumeUsedFabrication = computed(() => {
        return (lotId: number): number => {
            return volumeUsedFabricationMap.value.get(lotId) ?? 0;
        };
    });

    /**
     * Retourne le volume restant pré-calculé côté serveur (undefined = pas calculé ou hétérospermie)
     */
    const getVolumeRemaining = computed(() => {
        return (lotId: number): number | undefined => {
            return volumeRemainingMap.value.get(lotId);
        };
    });

    /**
     * Retourne le numéro de production en fonction de la date et de l'animal
     */
    const getAnimalLotNumber = computed(() => (animalId: number, dateProd: Date) => {
        let orderedProductionsWithTime = datas.value
            .filter(p => p.production?.animal?.id == animalId && getDateNumericFormat(p.dateLot) == getDateNumericFormat(dateProd))
            .sort((a, b) => new Date(a.dateLot).getTime() - new Date(b.dateLot).getTime())
            .map(p => ({
                animalId: p.production?.animal.id,
                dateProd: p.dateLot
            }));

        var numberProductionSameDay = orderedProductionsWithTime.length;

        if (numberProductionSameDay > 1) {
            numberProductionSameDay = orderedProductionsWithTime.findIndex(p => p.dateProd == dateProd) + 1;
        } else {
            numberProductionSameDay = 1;
        }

        return numberProductionSameDay;
    });

    /**
     * Retourne le numéro de production en fonction de la date
     */
    const getProductionNumber = computed(() => (dateProd: Date) => {
        let orderedProductionsWithTime = datas.value
            .filter(p => getDateNumericFormat(p.dateLot) == getDateNumericFormat(dateProd))
            .sort((a, b) => new Date(a.dateLot).getTime() - new Date(b.dateLot).getTime())
            .map(p => ({
                animalId: p.production?.animal.id,
                dateProd: p.dateLot
            }));

        var numberProductionSameDay = orderedProductionsWithTime.length;

        if (numberProductionSameDay > 1) {
            numberProductionSameDay = orderedProductionsWithTime.findIndex(p => p.dateProd == dateProd) + 1;
        } else {
            numberProductionSameDay = 1;
        }

        return numberProductionSameDay;
    });

    const getLotsDesc = computed((): ILot[] => {
        return [...datas.value]
            .map(l => ({ l, t: new Date(l.dateLot).getTime() }))
            .sort((a, b) => a.t - b.t || (a.l.geneticType?.code3 ?? '').localeCompare(b.l.geneticType?.code3 ?? ''))
            .map(x => x.l);
    });

    const todayAndYesterdayLot = computed(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        const startOfTomorrow = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

        return datas.value.filter(m => {
            let itemDate = new Date(m.dateLot);
            return itemDate >= startOfYesterday && itemDate < startOfTomorrow && m.isHeterospermie == true;
        });
    });

    const getLotsByAnimalAsc = computed(() => (idAnimal: number) => {
        return datas.value
            .filter(p => p.production?.animal?.id == idAnimal)
            .sort((a, b) => getDateNumericFormat(new Date(b.production?.dateProduction ?? 0)) - getDateNumericFormat(new Date(a.production?.dateProduction ?? 0)));
    });

    const getLotsAsc = computed((): ILot[] => {
        return [...datas.value]
            .map(l => ({ l, t: new Date(l.dateLot).getTime() }))
            .sort((a, b) => b.t - a.t || (b.l.geneticType?.code3 ?? '').localeCompare(a.l.geneticType?.code3 ?? ''))
            .map(x => x.l);
    });

    /**
     * Retourne la date effective d'un lot pour la navigation et le filtrage par date.
     * Pour les lots hétérospermie : dateLot (pas de production associée).
     * Pour les lots normaux : dateProduction du prélèvement, avec fallback sur dateLot.
     */
    const getLotEffectiveDate = (lot: ILot): Date => {
        if (lot.isHeterospermie) return new Date(lot.dateLot);
        return new Date(lot.production?.dateProduction ?? lot.dateLot);
    };

    const getLotsDuringTimespan = computed(() => (date1: Date, date2: Date): ILot[] => {
        const previousDay = new Date(date1);
        previousDay.setHours(0, 0, 0, 0);

        const nextDay = new Date(date2);
        nextDay.setHours(0, 0, 0, 0);

        if (datas.value.length > 0)
            return datas.value.filter((lot) => {
                const d = getLotEffectiveDate(lot);
                // Borne haute EXCLUSIVE (Mantis 0028188) : un lot du lendemain à 00:00 n'est pas du jour.
                return d < nextDay && d >= previousDay;
            });
        else
            return [];
    });

    const getPreviousLotDate = computed(() => (date: Date): Date | null => {
        const target = new Date(date);
        target.setHours(0, 0, 0, 0);
        const targetTime = target.getTime();

        let best = -Infinity;
        for (const lot of datas.value) {
            const d = getLotEffectiveDate(lot);
            d.setHours(0, 0, 0, 0);
            const t = d.getTime();
            if (t < targetTime && t > best) best = t;
        }
        return best > -Infinity ? new Date(best) : null;
    });

    const getNextLotDate = computed(() => (date: Date): Date | null => {
        const nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        nextDay.setHours(0, 0, 0, 0);
        const nextDayTime = nextDay.getTime();

        let best = Infinity;
        for (const lot of datas.value) {
            const d = getLotEffectiveDate(lot);
            d.setHours(0, 0, 0, 0);
            const t = d.getTime();
            if (t >= nextDayTime && t < best) best = t;
        }
        return best < Infinity ? new Date(best) : null;
    });

    // Actions

    /**
     * Hydrate une liste de lots en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param lots Liste de lots reçus de l'API avec des IDs
     * @returns Liste de lots avec les références des stores
     */
    async function hydrateLots(lots: ILotApiResponse[], dateFrom?: Date, perfPage?: string): Promise<ILot[]> {
        if (lots.length === 0) return [];
        const { start: perfStart, end: perfEnd } = usePerformanceTracker(perfPage);

        const centerStore = useCenterStore();
        const parameterStore = useParameterStore();
        const productionStore = useProductionStore();

        // Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        // Recharger productionStore si vide OU si le centre a changé OU si le load existant est plus partiel que demandé
        const needsCenterId = lots[0]?.centerId;
        const prodNeedsLoad =
            productionStore.datas.length === 0 ||
            (needsCenterId != null && productionStore.loadedCenterId !== needsCenterId) ||
            (dateFrom === undefined && productionStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && productionStore.loadedDateFrom !== undefined && productionStore.loadedDateFrom > dateFrom);
        if (prodNeedsLoad) {
            if (productionStore.isLoading) {
                // Un chargement est déjà en cours : attendre sa fin pour avoir un prodMap complet
                loadPromises.push(new Promise<void>(resolve => {
                    const interval = setInterval(() => {
                        if (!productionStore.isLoading) { clearInterval(interval); resolve(); }
                    }, 50);
                }));
            } else {
                // Toujours attendre le rechargement (même si le store a des données partielles)
                // pour éviter que prodMap soit construit depuis un jeu incomplet (ex: Production.vue
                // n'a chargé que les productions du jour → les lots anciens auraient production=undefined)
                loadPromises.push(productionStore.load({ page: 1, pageSize: 0, centerId: needsCenterId, dateFrom }, perfPage));
            }
        }
        if (loadPromises.length > 0) {
            perfStart(' ↳ hydrateLots : stores dépendants');
            await Promise.all(loadPromises);
            perfEnd(' ↳ hydrateLots : stores dépendants', 'api');
        }

        // Maps O(1)
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const paramMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const prodMap = new Map(productionStore.datas.map(p => [p.id, p]));

        // Hydrater en une seule passe
        const models: ILot[] = [];
        for (const lot of lots) {
            const model = { ...lot } as unknown as ILot;

            // producerCenterId/producerCenterCode (centre d'origine d'un lot externe, cf. ILot)
            // n'ont pas besoin de mapping ici : déjà des scalaires sur ILotApiResponse, copiés
            // tels quels par le spread ci-dessus (contrairement à centerId qui doit être résolu
            // en objet ICenter via centerMap).
            model.center = centerMap.get(lot.centerId) ?? model.center;
            model.geneticType = paramMap.get(lot.geneticTypeId) ?? model.geneticType;
            model.nn = lot.nnId ? paramMap.get(lot.nnId) : undefined;
            model.production = lot.productionId ? prodMap.get(lot.productionId) : undefined;
            // Utiliser les analyses du payload si présentes (envoyées par SignalR avec Include backend)
            model.analyses = (lot as ILotApiResponse).analyses?.map(a => ({ ...a } as unknown as IProductionAnalyse)) ?? [];

            models.push(model);
        }

        // Lots externes (animal d'un autre centre) : leur production n'est pas dans prodMap
        // car chargé avec le centerId courant. On les récupère individuellement par ID.
        const unresolvedLots = models.filter(m => !m.production && (m as any).productionId);
        if (unresolvedLots.length > 0) {
            await Promise.all(
                unresolvedLots.map(async m => {
                    const prodId = (m as any).productionId as number;
                    let production = productionStore.datas.find(p => p.id === prodId);
                    if (!production) {
                        const result = await ProductionService.getById(prodId);
                        if (!result.error && result.data) {
                            const [hydratedProduction] = await productionStore.hydrateProductions([
                                result.data as IProductionApiResponse,
                            ]);
                            const index = productionStore.datas.findIndex(p => p.id === prodId);
                            if (index >= 0) {
                                productionStore.datas[index] = hydratedProduction;
                            } else {
                                productionStore.datas.push(hydratedProduction);
                            }
                            production = hydratedProduction;
                        }
                    }
                    m.production = production;
                })
            );
        }

        return models;
    }

    /**
     * Mapping d'une réponse composite vers un ILot complet
     */
    function mapCompositeToLot(c: IEjaculateCompositeResponse, paramMap: Map<number, IParameter>): ILot {
        const lot: ILot = {
            id: c.id,
            lotNumber: c.lotNumber,
            dateLot: c.dateLot,
            isHeterospermie: c.isHeterospermie,
            armada: c.armada,
            nador: c.nador,
            concentration: c.concentration,
            primerVolume: c.primerVolume,
            index: c.index,
            center: { id: c.centerId } as ICenter,
            geneticType: c.geneticTypeId
                ? (paramMap.get(c.geneticTypeId) ?? { id: c.geneticTypeId, code3: c.geneticTypeCode3 } as IParameter)
                : {} as IParameter,
            nn: c.nnId
                ? (paramMap.get(c.nnId) ?? { id: c.nnId, code3: c.nnCode3, value: c.nnValue } as IParameter)
                : undefined,
            production: c.production ? {
                id: c.production.id,
                dateProduction: c.production.dateProduction,
                productionState: c.production.productionState,
                samplingState: c.production.samplingState,
                numberProduction: c.production.numberProduction,
                blankSample: c.production.blankSample,
                active: c.production.active,
                animalId: c.production.animal?.id ?? 0,
                animal: c.production.animal ? {
                    id: c.production.animal.id,
                    name: c.production.animal.name,
                    workNumber: c.production.animal.workNumber,
                    armada: c.production.animal.armada,
                    nador: c.production.animal.nador,
                    conservationAptitude: c.production.animal.conservationAptitudeCode3
                        ? { code3: c.production.animal.conservationAptitudeCode3 } as IParameter
                        : undefined,
                    geneticType: c.production.animal.geneticTypeId
                        ? (paramMap.get(c.production.animal.geneticTypeId) ?? { id: c.production.animal.geneticTypeId, code3: c.production.animal.geneticTypeCode3 } as IParameter)
                        : undefined,
                    nn: c.production.animal.nnId
                        ? (paramMap.get(c.production.animal.nnId) ?? { id: c.production.animal.nnId, value: c.production.animal.nnValue } as IParameter)
                        : undefined,
                } as unknown as IBoar : {} as IBoar,
                sample: c.production.sample ? {
                    concentration: c.production.sample.concentration,
                    secondaryConcentration: c.production.sample.secondaryConcentration,
                    volume: c.production.sample.volume,
                    secondaryVolume: c.production.sample.secondaryVolume,
                    absorbance: c.production.sample.absorbance,
                    secondaryAbsorbance: c.production.sample.secondaryAbsorbance,
                } as IProductionSample : undefined,
                static: c.production.static ? {
                    nador: c.production.static.nador,
                    armada: c.production.static.armada,
                    index: c.production.static.index,
                    nnId: c.production.static.nnId,
                    NN: c.production.static.nnId
                        ? (paramMap.get(c.production.static.nnId) ?? { id: c.production.static.nnId, code3: c.production.static.nnCode3 } as IParameter)
                        : undefined,
                    deviderConcentration: c.production.static.deviderConcentration,
                    prcPredilution: c.production.static.prcPredilution,
                    geneticTypeId: c.production.static.geneticTypeId,
                    geneticType: c.production.static.geneticTypeId
                        ? (paramMap.get(c.production.static.geneticTypeId) ?? { id: c.production.static.geneticTypeId, code3: c.production.static.geneticTypeCode3 } as IParameter)
                        : undefined,
                    // Aptitude à la production : reprise à la mise en stock au lieu de celle du
                    // verrat modifié depuis (Mantis 0026876).
                    animalClassId: c.production.static.animalClassId,
                    animalClass: c.production.static.animalClassId
                        ? (paramMap.get(c.production.static.animalClassId) ?? { id: c.production.static.animalClassId, code3: c.production.static.animalClassCode3 } as IParameter)
                        : undefined,
                } as IProductionStatic : undefined,
                dilutorBaths: (c.production.dilutorBaths ?? []).map(db => ({
                    volume: db.volume,
                    dilutorBath: db.name ? { name: db.name } as any : undefined,
                } as IProductionDilutorBath)),
                // Fields not in composite but needed for type compatibility
                agentDilutor: {} as any,
                agentSampler: {} as any,
                technique: {} as any,
                // Centre de la PRODUCTION : il diffère de celui du lot pour un lot reçu. Repli sur le
                // centre du lot pour une réponse d'un serveur antérieur au champ.
                centerId: c.production.centerId ?? c.centerId,
                center: { id: c.production.centerId ?? c.centerId } as ICenter,
            } as unknown as IProduction : undefined,
            isExternal: c.isExternal,
            sourceLotId: c.sourceLotId,
            producerCenterId: c.producerCenterId,
            producerCenterCode: c.producerCenterCode,
            volumeRemaining: c.volumeRemaining,
            analyses: (c.analyses ?? []).map(a => ({
                id: a.id,
                prcLiving: a.prcLiving,
                motility: a.motility,
                prcNormal: a.prcNormal,
                prcUtile: a.prcUtile,
                prcMotility: a.prcMotility,
                totalmml: a.totalmml,
                isUsedCasa: a.isUsedCasa,
                isControl: a.isControl,
            } as IProductionAnalyse)),
        };
        return lot;
    }

    /**
     * Mapping d'un dispatch composite vers IDispatchManufactoring
     */
    function mapCompositeToDispatch(
        d: IEjaculateDispatchResponse,
        paramMap: Map<number, IParameter>,
        lotMap: Map<number, ILot>,
        mpMap: Map<number, IManufactoringProduction>,
    ): IDispatchManufactoring {
        return {
            id: d.id,
            volume: d.volume,
            numberDosesManufactured: d.numberDosesManufactured,
            numberDosesTheoretical: d.numberDosesTheoretical,
            datePackaging: d.datePackaging,
            dateManufactoring: d.dateManufactoring,
            dateValidation: d.dateValidation,
            dateCancellation: d.dateCancellation,
            isValidated: d.isValidated,
            isCanceled: d.isCanceled,
            isArmada: d.isArmada,
            archived: d.archived,
            primerVolume: d.primerVolume,
            packagingId: d.packagingId,
            packaging: paramMap.get(d.packagingId) ?? { id: d.packagingId, code3: d.packagingCode3 } as IParameter,
            belongsToLotId: d.belongsToLotId,
            belongsToLot: d.belongsToLotId ? lotMap.get(d.belongsToLotId) : undefined,
            partOfLotId: d.partOfLotId,
            partOfLot: d.partOfLotId ? lotMap.get(d.partOfLotId) : undefined,
            parentId: d.parentId,
            parent: undefined, // Linked in a second pass
            manufactoringProductionId: d.manufactoringProductionId,
            manufactoringProduction: d.manufactoringProductionId ? mpMap.get(d.manufactoringProductionId) : undefined,
            numberManufactoring: d.numberManufactoring,
            centerId: d.centerId,
            center: { id: d.centerId } as ICenter,
            volumeAvailable: d.volumeAvailable,
            children: [], // Linked in a second pass
            active: true, // Le composite ne retourne que les actifs (WHERE Active = true)
        } as IDispatchManufactoring;
    }

    /**
     * Chargement composite des données d'éjaculats via un seul endpoint.
     * Remplace les appels séparés à Production, Lot, ManufactoringProduction et DispatchManufactoring.
     * Peuple aussi les stores dispatch et manufactoringProduction pour que les getters existants fonctionnent.
     */
    async function loadEjaculateData(centerId: number, dateFrom?: Date, perfPage?: string, minVolume?: number) {
        const { start, end } = usePerformanceTracker(perfPage);
        isLoading.value = true;

        start('GET /Lot/ejaculate (réseau)');
        const result = await LotService.getEjaculateData(centerId, dateFrom, minVolume);
        end('GET /Lot/ejaculate (réseau)', 'api');

        if (!result.error && result.data) {
            const composites = result.data as IEjaculateCompositeResponse[];

            start('Mapping composite → stores');

            // 1. Map composite → ILot[]
            const parameterStore = useParameterStore();
            const paramMap = new Map(parameterStore.datas.map(p => [p.id, p]));

            const lots: ILot[] = composites.map(c => mapCompositeToLot(c, paramMap));
            datas.value = lots;

            // Peupler la map volumeUsedFabrication (pré-calculé côté serveur)
            const vufMap = new Map<number, number>();
            const vrMap = new Map<number, number | undefined>();
            for (const c of composites) {
                vufMap.set(c.id, c.volumeUsedFabrication);
                vrMap.set(c.id, c.volumeRemaining);
            }
            volumeUsedFabricationMap.value = vufMap;
            volumeRemainingMap.value = vrMap;

            // 2. Peupler manufactoringProductionStore.datas
            const mpStore = useManufactoringProductionStore();
            const mpItems: IManufactoringProduction[] = [];
            // Créer un lotMap pour le dispatch hydration
            const lotMap = new Map(lots.map(l => [l.id, l]));

            for (const c of composites) {
                if (c.manufactoringProduction) {
                    const mp = c.manufactoringProduction;
                    const lot = lotMap.get(c.id);
                    mpItems.push({
                        id: mp.id,
                        productionId: mp.productionId,
                        production: lot?.production,
                        concentrationFinale: mp.concentrationFinale,
                        volumeAmorce: mp.volumeAmorce,
                        volumeFinal: mp.volumeFinal,
                        numberFabrication: mp.numberFabrication,
                        isHetero: mp.isHetero,
                    } as IManufactoringProduction);
                }
            }
            mpStore.datas = mpItems;
            // Signaler la date de couverture pour que hydrateDispatchManufactorings sache si un rechargement est nécessaire
            mpStore.loadedDateFrom = dateFrom;

            // 3. Peupler dispatchManufactoringStore.datas
            const dispStore = useDispatchManufactoringStore();
            const allDispatches: IDispatchManufactoring[] = [];
            const mpMap = new Map(mpItems.map(m => [m.id, m]));

            for (const c of composites) {
                const lot = lotMap.get(c.id);

                // BelongsToLot dispatches
                for (const d of c.belongsToLotDispatches ?? []) {
                    const dispatch = mapCompositeToDispatch(d, paramMap, lotMap, mpMap);
                    allDispatches.push(dispatch);
                }

                // PartOfLot dispatches
                for (const d of c.partOfLotDispatches ?? []) {
                    const dispatch = mapCompositeToDispatch(d, paramMap, lotMap, mpMap);
                    allDispatches.push(dispatch);
                }
            }

            // Dédupliquer par id (un dispatch peut être belongsTo d'un lot ET partOf d'un autre)
            const dispatchMap = new Map<number, IDispatchManufactoring>();
            for (const d of allDispatches) {
                dispatchMap.set(d.id, d);
            }
            // Relier parent/children
            for (const d of dispatchMap.values()) {
                if (d.parentId) {
                    d.parent = dispatchMap.get(d.parentId);
                    if (d.parent && !d.parent.children.find(ch => ch.id === d.id)) {
                        d.parent.children.push(d);
                    }
                }
            }
            dispStore.datas = Array.from(dispatchMap.values());
            dispStore.loadedCenterId = centerId;
            // Fenetre reellement couverte par le composite : sans ce marqueur, une vue qui a
            // besoin d une periode PLUS LARGE croirait son cache suffisant et afficherait un
            // jeu tronque.
            dispStore.loadedDateFrom = dateFrom;
            dispStore.loadedViaComposite = true;

            end('Mapping composite → stores', 'mapping');
        } else {
            datas.value = [];
        }

        isLoading.value = false;
        loadedCenterId.value = centerId;
        loadedDateFrom.value = dateFrom; // Requis pour que Production.vue sache que ces lots sont filtrés par date
        // Ce chemin (Prélèvements en attente) ne demande jamais le centre producteur : le cache
        // qu'il laisse derrière lui ne peut pas servir à la colonne « Centre » du Suivi de qualité.
        loadedWithProducerCenter.value = false;
        return result;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: ILotFilter, centerId?: number, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);
        isLoading.value = true;

        // Un load change de centre ou élargit la fenêtre : on redonne sa chance à chaque id mémorisé.
        unresolvableLotIds.clear();

        start('GET /Lot (réseau)');
        let result = await LotService.getAll<ILotFilter, ILotApiResponse>(filter);
        end('GET /Lot (réseau)', 'api');

        datas.value = [];

        if (!result.error) {
            start('hydrateLots()');
            datas.value = await hydrateLots(result.data as ILotApiResponse[], filter.dateFrom, perfPage);
            end('hydrateLots()', 'mapping');
        }

        isLoading.value = false;
        // Le centre vient du filtre quand l'appelant ne passe pas le param positionnel `centerId`
        // (cas de SearchAnimalModal). Sans ce fallback, loadedCenterId restait undefined et le garde
        // `lotAlreadyLoaded` de Production.vue était toujours faux → rechargement systématique de tous
        // les lots + productions du centre à chaque ouverture.
        loadedCenterId.value = centerId ?? filter.centerId;
        loadedDateFrom.value = filter.dateFrom;
        loadedWithProducerCenter.value = filter.includeProducerCenter === true;
        return result;
    }

    /**
     * Récupération d'un lot par son identifiant
     */
    async function get(id: number, searchStore: boolean = false) {
        if (searchStore) {
            const lot = await searchStoreById(id);
            if (lot) {
                current.value = lot;
                itemForm.value = toForm(lot);
                return new ServiceOperationResult<ILot>(false, lot);
            }
        }

        let result = await LotService.getById<ILotApiResponse>(id);

        if (!result.error) {
            const [modelData] = await hydrateLots([result.data as ILotApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;
            else
                datas.value.push(modelData);
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    /**
     * S'assure qu'un lot est présent dans le store (le charge par id sinon) et le retourne.
     * NE touche PAS current/itemForm (contrairement à get) : sert à résoudre un lot manquant
     * lors d'une hydratation (ex : lot hétérospermie créé sur un autre poste et reçu via SignalR,
     * absent du lotStore déjà chargé). Mirror de manufactoringProductionStore.ensureLoadedById.
     */
    async function ensureLoadedById(id: number): Promise<ILot | undefined> {
        const existing = datas.value.find(l => l.id === id);
        if (existing) return existing;
        if (unresolvableLotIds.has(id)) return undefined;

        const result = await LotService.getById<ILotApiResponse>(id);
        if (result.error || !result.data) {
            unresolvableLotIds.add(id);
            return undefined;
        }

        const [model] = await hydrateLots([result.data as ILotApiResponse], loadedDateFrom.value);
        if (datas.value.findIndex(l => l.id === model.id) === -1) {
            datas.value.push(model);
        }
        return model;
    }

    /**
     * Variante en masse de {@link ensureLoadedById} : une requête `Ids=` pour tous les lots
     * manquants au lieu d'un GET par lot, puis **repli unitaire sur ce que le batch n'a pas rendu**.
     *
     * Le repli n'est pas une précaution de principe : `/Lot/search` applique
     * `.Where(a => a.Active ?? true)` sans condition (LotRepository), alors que `GET /Lot/{id}`
     * ne l'applique pas. Un lot supprimé résolu aujourd'hui par id disparaîtrait donc du batch,
     * et son dispatch serait exclu du suivi de fabrication — le symptôme du Mantis 0028085.
     * Le repli reste borné : les ids qu'il ne résout pas non plus sont mémorisés.
     */
    async function ensureLoadedByIds(ids: number[], perfPage?: string): Promise<ILot[]> {
        const wanted = [...new Set(ids)].filter(id => !!id && !unresolvableLotIds.has(id));
        if (wanted.length === 0) return [];

        const resolved: ILot[] = [];
        const missing: number[] = [];
        for (const id of wanted) {
            const existing = datas.value.find(l => l.id === id);
            if (existing) resolved.push(existing);
            else missing.push(id);
        }
        if (missing.length === 0) return resolved;

        const result = await LotService.getAll<ILotFilter, ILotApiResponse>({
            page: 1,
            pageSize: 0,
            ids: missing,
        });

        const returned = new Set<number>();
        // Erreur réseau : on ne mémorise rien et on laisse le repli unitaire tenter sa chance.
        if (!result.error && result.data) {
            const models = await hydrateLots(result.data as ILotApiResponse[], loadedDateFrom.value, perfPage);
            for (const model of models) {
                if (datas.value.findIndex(l => l.id === model.id) === -1) {
                    datas.value.push(model);
                }
                resolved.push(model);
                returned.add(model.id);
            }
        }

        const stillMissing = missing.filter(id => !returned.has(id));
        if (stillMissing.length > 0) {
            const fallback = await Promise.all(stillMissing.map(id => ensureLoadedById(id)));
            fallback.filter((l): l is ILot => l !== undefined).forEach(l => resolved.push(l));
        }

        return resolved;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as ILot);
        return current.value;
    }

    /**
     * Compare deux valeurs de champ d'analyse par valeur, pas par référence : le snapshot passe
     * par un clone JSON qui transforme les Date en chaînes ISO, une comparaison stricte (===)
     * les considèrerait donc toujours comme "différentes" même sans modification réelle.
     */
    function analyseFieldsEqual(a: unknown, b: unknown): boolean {
        if (a instanceof Date || b instanceof Date) {
            return new Date(a as string | Date).getTime() === new Date(b as string | Date).getTime();
        }
        return a === b;
    }

    /**
     * Fusionne les analyses reçues du serveur avec la saisie en cours, pour ne pas écraser une
     * frappe utilisateur faite pendant l'aller-retour réseau d'un save() (ex : %Vivant/Motilité
     * saisis pendant qu'un save() déclenché par la pré-dilution est encore en vol — Mantis
     * 0028309). On compare la valeur ACTUELLE de chaque champ à sa valeur au moment de l'envoi
     * (snapshot) : si elles diffèrent, l'utilisateur l'a modifiée pendant la requête → on garde
     * sa saisie plutôt que la valeur serveur, qui reflète l'état d'avant la frappe.
     */
    function mergeAnalysesPreservingLocalEdits(
        serverAnalyses: IProductionAnalyseForm[],
        snapshotBeforeSave: IProductionAnalyseForm[],
        currentAnalyses: IProductionAnalyseForm[],
    ): IProductionAnalyseForm[] {
        return serverAnalyses.map((serverAnalyse, i) => {
            const currentAnalyse = currentAnalyses[i];
            const snapshotAnalyse = snapshotBeforeSave[i];
            if (!currentAnalyse || !snapshotAnalyse || currentAnalyse.id !== snapshotAnalyse.id) {
                return serverAnalyse;
            }

            const merged = { ...serverAnalyse };
            (Object.keys(currentAnalyse) as (keyof IProductionAnalyseForm)[]).forEach(key => {
                if (!analyseFieldsEqual(currentAnalyse[key], snapshotAnalyse[key])) {
                    (merged as any)[key] = currentAnalyse[key];
                }
            });
            return merged;
        });
    }

    /**
     * Mise à jour ou création d'un objet
     */
    /** Création de lot déjà partie et pas encore revenue (cf. save, Mantis 0028526). */
    let createInFlight: Promise<ServiceOperationResult<ILotApiResponse | IDataOperationResult<ILotApiResponse>>> | null = null;

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<ILotApiResponse | IDataOperationResult<ILotApiResponse>>;

        // Snapshot des analyses juste avant l'envoi réseau : permet, à la réponse, de détecter les
        // champs que l'utilisateur a modifiés PENDANT que la requête était en vol.
        const analysesSnapshotBeforeSave = itemForm.value.analyses
            ? (JSON.parse(JSON.stringify(itemForm.value.analyses)) as IProductionAnalyseForm[])
            : [];

        if (itemForm.value.id > 0) {
            result = await LotService.update(itemForm.value);

            if (!result.error) {
                const [modelData] = await hydrateLots([result.data as ILotApiResponse]);
                current.value = modelData;
                const analysesBeforeToForm = itemForm.value.analyses ?? [];
                itemForm.value = toForm(current.value);
                itemForm.value.analyses = mergeAnalysesPreservingLocalEdits(
                    itemForm.value.analyses ?? [],
                    analysesSnapshotBeforeSave,
                    analysesBeforeToForm,
                );
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        datas.value.push(current.value);
                    }
                }
            }
        } else {
            // Mantis 0028526 : la création est décidée sur `id <= 0`, et l'id n'est posé qu'au RETOUR
            // réseau. Deux appels concurrents — la modale de pré-dilution émet `decreaseBathVolume`
            // depuis la validation manuelle ET depuis l'arrêt de pompe — voyaient donc tous les deux
            // un id à 0 et créaient chacun un lot. L'écran « Prélèvements en attente » affiche une
            // ligne par lot : une seule dilution réelle y apparaissait en double, et les versements
            // suivants ne partaient que sur l'un des deux.
            // Les appels concurrents partagent la requête déjà en vol : ils portent le même lot pour
            // la même production (même formulaire, id encore 0), donc la même réponse les sert tous.
            if (createInFlight) {
                return await createInFlight;
            }

            const pendingCreate = (async () => {
                // Générique explicite : hors d'une affectation directe à `result`, TypeScript ne peut
                // plus déduire R du contexte et retomberait sur sa contrainte (IUniqueId).
                const created = await LotService.create<ILotForm, ILotApiResponse>(itemForm.value);
                if (!created.error) {
                    const [modelData] = await hydrateLots([created.data as ILotApiResponse]);
                    current.value = modelData;
                    const analysesBeforeToForm = itemForm.value.analyses ?? [];
                    itemForm.value = toForm(current.value);
                    itemForm.value.analyses = mergeAnalysesPreservingLocalEdits(
                        itemForm.value.analyses ?? [],
                        analysesSnapshotBeforeSave,
                        analysesBeforeToForm,
                    );
                }
                return created;
            })();

            createInFlight = pendingCreate;
            try {
                result = await pendingCreate;
            } finally {
                createInFlight = null;
            }
        }
        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteLot(idLot: number) {
        let result = await LotService.delete<ILot>(idLot);

        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == idLot);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    function createLotFromProduction(production: IProduction) {
        itemForm.value.isHeterospermie = false;
        itemForm.value.productionId = production.id;
        itemForm.value.centerId = production.center.id;
        itemForm.value.concentration = production.sample?.secondaryConcentration > 0 ? production.sample?.secondaryConcentration : production.sample?.concentration ?? 0;
        itemForm.value.primerVolume = 0;
        itemForm.value.geneticTypeId = production.animal.geneticType?.id ?? 0;
        itemForm.value.nador = production.static?.nador ?? false;
        itemForm.value.index = production.static?.index ?? 0;
        itemForm.value.nnId = production.static?.NN?.id;
        itemForm.value.dateLot = production.dateProduction;
        itemForm.value.armada = production.static?.armada ?? false;
        itemForm.value.isHeterospermie = false;
    }

    function toForm(item: ILot): ILotForm {
        const analyseStore = useProductionAnalyseStore();
        const form = {} as ILotForm;
        form.id = item.id;
        form.centerId = item.center?.id ?? 0;
        form.lotNumber = item.lotNumber;
        form.concentration = item.concentration;
        form.isHeterospermie = item.isHeterospermie;
        form.primerVolume = item.primerVolume;
        form.geneticTypeId = item.geneticType?.id ?? 0;
        form.nador = item.nador;
        form.armada = item.armada;
        form.dateLot = item.dateLot;
        // item.production peut être undefined si le lot a été hydraté avant que productionStore soit prêt (race condition)
        // Dans ce cas, on récupère l'ID brut depuis la réponse API (présent via le spread { ...lot })
        const rawProductionId = item.production?.id ?? (item as any).productionId as number | undefined;
        if (rawProductionId) {
            form.productionId = rawProductionId;
        }
        form.belongsToLots = [] as IDispatchManufactoringForm[];
        form.partOfLots = [] as IDispatchManufactoringForm[];
        const mappedAnalyses = (item.analyses ?? []).map(a => analyseStore.toForm(a)) as IProductionAnalyseForm[];
        // Ensure at least one analysis exists to avoid undefined access in Production.vue
        // hydrateLots always returns analyses=[] — on vérifie d'abord le store (alimenté via SignalR)
        if (mappedAnalyses.length > 0) {
            form.analyses = mappedAnalyses;
        } else {
            const existingAnalyses = analyseStore.getSortedByDateControleAndLot(item.id, false);
            const inProgressAnalyses = itemForm.value?.id === item.id
                ? (itemForm.value?.analyses ?? [])
                : [];
            if (existingAnalyses.length > 0) {
                form.analyses = existingAnalyses.map(a => analyseStore.toForm(a));
            } else if (inProgressAnalyses.length > 0) {
                // Ni le serveur ni le store n'ont d'analyse pour ce lot : conserver celle EN COURS DE
                // SAISIE plutôt que la remplacer par un formulaire vierge (ce que faisait le fallback
                // ci-dessous). Cas réel : la pré-dilution déclenche une sauvegarde du lot, l'opérateur
                // continue de saisir « % vivant » et « Motilité » pendant l'aller-retour réseau, et à
                // la réponse `save()` réassigne itemForm — les deux champs étaient vidés et il fallait
                // les ressaisir (Mantis 0028309).
                // Garde sur l'id : à un CHANGEMENT de lot, il ne faut rien reporter de l'ancien.
                form.analyses = inProgressAnalyses;
            } else if (rawProductionId) {
                // Fallback analyse vide pour les lots monospermie (Production.vue accède à analyses[0])
                // Utilise rawProductionId pour couvrir le cas où item.production est undefined (race condition)
                form.analyses = [analyseStore.newForm(item.id)];
            } else {
                form.analyses = [];
            }
        }
        form.index = item.index;
        if (item.nn)
            form.nnId = item.nn?.id;
        itemForm.value = form;
        return form;
    }

    function newForm(): ILotForm {
        itemForm.value = {
            id: 0,
            centerId: 0,
            lotNumber: "",
            concentration: 0,
            isHeterospermie: false,
            primerVolume: 0,
            geneticTypeId: 0,
            belongsToLots: [],
            analyses: [],
            partOfLots: [],
            index: 0,
            nnId: undefined,
            nador: false,
            armada: false,
            productionId: undefined,
            dateLot: new Date()
        } as ILotForm;
        return itemForm.value;
    }

    function newItem(): ILot {
        current.value = {
            id: 0,
            center: {} as ICenter,
            lotNumber: "",
            concentration: 0,
            isHeterospermie: false,
            primerVolume: 0,
            geneticType: {} as IParameter,
            production: {} as IProduction,
            analyses: [] as IProductionAnalyse[],
            index: 0,
            nador: false,
            nn: undefined,
            armada: false,
            dateLot: new Date(),
        } as ILot;

        itemForm.value = toForm(current.value);
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        loadedCenterId,
        loadedDateFrom,
        loadedWithProducerCenter,
        signalRVersion,

        // Getters
        getAnimalLotNumber,
        getProductionNumber,
        getLotsDesc,
        todayAndYesterdayLot,
        getLotsByAnimalAsc,
        getLotsAsc,
        getLotsDuringTimespan,
        getPreviousLotDate,
        getNextLotDate,
        getVolumeUsedFabrication,
        getVolumeRemaining,

        // Actions
        hydrateLots,
        loadEjaculateData,
        load,
        get,
        ensureLoadedById,
        ensureLoadedByIds,
        searchStoreById,
        save,
        delete: deleteLot,
        createLotFromProduction,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajLot", async (payload: IPayloadStore) => {
    const store = useLotStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const lotData = payload.data as ILotApiResponse;

                // Ignorer les lots arrivés via synchro RabbitMQ qui n'appartiennent pas
                // au centre chargé (ni à l'un de ses sous-centres). Sans ce filtre, un lot
                // produit sur un autre centre (ex: BCL) apparaît dans le suivi qualité d'un
                // centre tiers (ex: STE) dès sa création, avant tout transfert.
                if (store.loadedCenterId !== undefined && lotData.centerId !== store.loadedCenterId) {
                    const centerStore = useCenterStore();
                    const isSubCenter = centerStore.datas.some(c => c.id === lotData.centerId && c.parentId === store.loadedCenterId);
                    if (!isSubCenter) break;
                }

                // Garantir que la production est dans le store avant d'hydrater le lot.
                // Race condition : MajLot peut arriver avant MajProduction → prodMap vide →
                // model.production = undefined → Ejaculate.filtering = false → lot invisible.
                if (lotData.productionId) {
                    const productionStore = useProductionStore();
                    if (!productionStore.datas.find(p => p.id == lotData.productionId)) {
                        await productionStore.get(lotData.productionId);
                    }
                }
                const [hydrated] = await store.hydrateLots([lotData]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateLots([payload.data as ILotApiResponse]);
                // Une diffusion sans centre producteur (émetteur antérieur, synchro) ne doit pas effacer
                // celui déjà résolu : la ligne retomberait sur le centre receveur (Mantis 0028498).
                const previous = store.datas[idx];
                hydrated.producerCenterId ??= previous?.producerCenterId;
                hydrated.producerCenterCode ??= previous?.producerCenterCode;
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
    store.signalRVersion++;
});

